/**
 * Super Admin - Reservation Details (Full Page)
 * View complete reservation information with all guest details
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { superAdminAPI } from "utils/api";

function ReservationDetails() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [reservation, setReservation] = useState(null);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    const fetchReservation = async () => {
      try {
        setLoading(true);
        setMessage({ type: "", text: "" });
        const res = await superAdminAPI.getReservationById(id);
        const reservationData = res.data?.reservation || res.reservation || res.data;
        setReservation(reservationData);
      } catch (error) {
        console.error("Error fetching reservation details:", error);
        setMessage({ type: "error", text: error.message || "Failed to load reservation details" });
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchReservation();
  }, [id]);

  const currency = reservation?.pricing?.currency || "EGP";
  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
    }).format(amount || 0);
  const formatDateTime = (date) =>
    date
      ? new Date(date).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })
      : "—";

  // Calculate platform fee and insurance deposit
  const totalAmount = reservation?.pricing?.totalAmount || 0;
  const profitPercentage = reservation?.property?.profitPercentage || 0;
  const platformFee = (totalAmount * profitPercentage) / 100;
  const insuranceDeposit =
    reservation?.pricing?.insurancePrice || reservation?.property?.insurancePrice || 0;

  const statusChip = useMemo(() => {
    const s = reservation?.status || "Confirmed";
    let label = s.replace(/_/g, " ").toUpperCase();
    let color = "#1976d2"; // Default blue
    if (s === "CheckedIn") {
      label = "CHECKED IN";
      color = "#4caf50";
    } else if (s === "CheckedOut") {
      label = "CHECKED OUT";
      color = "#9e9e9e";
    } else if (s === "Cancelled" || s === "Refunded") {
      color = "#f44336";
    }
    return { label, color };
  }, [reservation]);

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3} display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox
          mb={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => navigate("/super-admin/reservations")}
            startIcon={<Icon>arrow_back</Icon>}
          >
            Back to Reservations
          </MDButton>
          {reservation?.reservationNumber && (
            <Chip label={reservation.reservationNumber} size="small" sx={{ fontWeight: 700 }} />
          )}
        </MDBox>

        {message.text && (
          <Alert severity={message.type === "error" ? "error" : "success"} sx={{ mb: 3 }}>
            {message.text}
          </Alert>
        )}

        {!reservation ? (
          <Card sx={{ p: 4, borderRadius: 3 }}>
            <MDTypography variant="h6" fontWeight="bold">
              Reservation not found
            </MDTypography>
          </Card>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ p: 3, borderRadius: 3 }}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  flexWrap="wrap"
                  gap={2}
                >
                  <MDBox>
                    <MDTypography variant="h5" fontWeight="bold" mb={0.5}>
                      {reservation.property?.title?.en || reservation.property?.title || "N/A"}
                    </MDTypography>
                    {reservation.property?.shortId && (
                      <MDTypography variant="body2" color="text.secondary">
                        ID: {reservation.property.shortId}
                      </MDTypography>
                    )}
                  </MDBox>
                  <Chip
                    label={statusChip.label}
                    sx={{ bgcolor: statusChip.color, color: "white", fontWeight: 700 }}
                    size="small"
                  />
                </MDBox>
              </Card>
            </Grid>

            {(() => {
              const agent =
                reservation.assignedCheckInOutAgent ||
                reservation.property?.assignedCheckInOutAgent;
              return agent ? (
                <Grid item xs={12}>
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      bgcolor: "info.lighter",
                      border: "1px solid",
                      borderColor: "info.light",
                    }}
                  >
                    <MDTypography
                      variant="h6"
                      fontWeight="bold"
                      mb={2}
                      color="info.dark"
                      display="flex"
                      alignItems="center"
                      gap={1}
                    >
                      <Icon sx={{ fontSize: 22 }}>person</Icon>
                      Check-in/Check-out Agent (this property)
                    </MDTypography>
                    <Divider sx={{ mb: 2 }} />
                    <MDBox display="flex" flexDirection="column" gap={1}>
                      <MDBox display="flex" justifyContent="space-between">
                        <MDTypography variant="body2" color="text.secondary">
                          Name
                        </MDTypography>
                        <MDTypography variant="body2" fontWeight="medium">
                          {agent.fullName || agent.firstName || "—"}
                        </MDTypography>
                      </MDBox>
                      <MDBox display="flex" justifyContent="space-between">
                        <MDTypography variant="body2" color="text.secondary">
                          Email
                        </MDTypography>
                        <MDTypography variant="body2" fontWeight="medium">
                          {agent.email || "—"}
                        </MDTypography>
                      </MDBox>
                      <MDBox display="flex" justifyContent="space-between">
                        <MDTypography variant="body2" color="text.secondary">
                          Phone
                        </MDTypography>
                        <MDTypography variant="body2" fontWeight="medium">
                          {agent.phone || agent.companyNumber || "—"}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </Card>
                </Grid>
              ) : null;
            })()}

            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, borderRadius: 3, height: "100%" }}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="info">event</Icon> Booking Details
                </MDTypography>
                <Divider sx={{ mb: 2 }} />
                <MDBox display="flex" justifyContent="space-between" mb={1}>
                  <MDTypography variant="body2" color="text.secondary">
                    Check-in
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {new Date(reservation.checkInDate).toLocaleDateString()}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" justifyContent="space-between" mb={1}>
                  <MDTypography variant="body2" color="text.secondary">
                    Check-out
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {new Date(reservation.checkOutDate).toLocaleDateString()}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" justifyContent="space-between">
                  <MDTypography variant="body2" color="text.secondary">
                    Guests
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {reservation.numberOfGuests || 0}
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, borderRadius: 3, height: "100%" }}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="success">paid</Icon> Finance Details
                </MDTypography>
                <Divider sx={{ mb: 2 }} />
                <MDBox display="flex" justifyContent="space-between" mb={1}>
                  <MDTypography variant="body2" color="text.secondary">
                    Total Amount (Client Paid)
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="bold" color="success.main">
                    {formatCurrency(totalAmount)}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" justifyContent="space-between" mb={1}>
                  <MDTypography variant="body2" color="text.secondary">
                    Platform Revenue (Fee)
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="bold" color="warning.main">
                    {formatCurrency(platformFee)}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" justifyContent="space-between" mb={1}>
                  <MDTypography variant="body2" color="text.secondary">
                    Insurance Deposit
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {formatCurrency(insuranceDeposit)}
                  </MDTypography>
                </MDBox>
                <Divider sx={{ my: 2 }} />
                <MDBox display="flex" justifyContent="space-between" mb={1}>
                  <MDTypography variant="body2" color="text.secondary" fontWeight="medium">
                    Property Owner Revenue
                  </MDTypography>
                  <MDTypography variant="body1" fontWeight="bold" color="info.main">
                    {formatCurrency(totalAmount - platformFee)}
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>

            {/* Inspector Note & Special Requests - Always visible */}
            <Grid item xs={12}>
              <Card sx={{ p: 3, borderRadius: 3, border: "1px solid", borderColor: "info.light" }}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="info">notes</Icon> Inspector Note & Special Requests
                </MDTypography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <MDBox>
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={1}
                        color="text.secondary"
                      >
                        Inspector Note
                      </MDTypography>
                      <MDTypography variant="body2">
                        {(reservation.checkIn && reservation.checkIn.notes) ||
                          "No inspector note recorded."}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDBox>
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={1}
                        color="text.secondary"
                      >
                        Special Requests
                      </MDTypography>
                      <MDTypography variant="body2">
                        {reservation.specialRequests || "No special requests from guest."}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            {/* Guest Names & Photo IDs - Primary section (taken by check-in agent) */}
            <Grid item xs={12}>
              <Card sx={{ p: 3, borderRadius: 3 }}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="info">badge</Icon> Guest Names & Photo IDs (from Check-In)
                </MDTypography>
                <MDTypography variant="caption" color="text.secondary" display="block" mb={2}>
                  Guest details and ID photos captured when the check-in agent performed check-in
                </MDTypography>
                <Divider sx={{ mb: 2 }} />
                {(() => {
                  const guests = reservation.checkIn?.guests;
                  const hasGuests = Array.isArray(guests) && guests.length > 0;
                  // Fallback: when checked in but no guest IDs recorded, show client as primary guest
                  const clientName =
                    reservation.client?.fullName ||
                    [reservation.client?.firstName, reservation.client?.lastName]
                      .filter(Boolean)
                      .join(" ") ||
                    reservation.client?.email;
                  const fallbackGuests =
                    !hasGuests &&
                    (reservation.status === "CheckedIn" || reservation.status === "CheckedOut") &&
                    clientName
                      ? [{ name: clientName, idFrontPhoto: null, idBackPhoto: null }]
                      : [];
                  const displayGuests = hasGuests ? guests : fallbackGuests;
                  return displayGuests.length > 0 ? (
                    <Grid container spacing={3}>
                      {displayGuests.map((guest, index) => (
                        <Grid item xs={12} md={6} lg={4} key={index}>
                          <Card
                            sx={{
                              p: 2.5,
                              borderRadius: 2,
                              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                              border: "1px solid",
                              borderColor: "grey.200",
                              "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,0.1)" },
                            }}
                          >
                            <MDTypography
                              variant="subtitle1"
                              fontWeight="bold"
                              mb={1.5}
                              color="info.main"
                            >
                              Guest {index + 1}: {guest.name || "N/A"}
                            </MDTypography>

                            {/* ID photos side by side - forced horizontal layout */}
                            {(guest.idFrontPhoto || guest.idBackPhoto) && (
                              <MDBox
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  gap: 2,
                                  alignItems: "flex-start",
                                  mt: 1.5,
                                  flexWrap: "nowrap",
                                }}
                              >
                                {guest.idFrontPhoto && (
                                  <MDBox sx={{ flexShrink: 0 }}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      fontWeight="medium"
                                      display="block"
                                      mb={0.5}
                                    >
                                      ID Front
                                    </MDTypography>
                                    <MDBox
                                      component="img"
                                      src={guest.idFrontPhoto}
                                      alt={`Guest ${index + 1} ID Front`}
                                      sx={{
                                        width: 140,
                                        height: 95,
                                        objectFit: "cover",
                                        borderRadius: 1.5,
                                        border: "1px solid",
                                        borderColor: "grey.300",
                                        cursor: "pointer",
                                        transition: "opacity 0.2s",
                                        "&:hover": { opacity: 0.85 },
                                      }}
                                      onClick={() => window.open(guest.idFrontPhoto, "_blank")}
                                    />
                                  </MDBox>
                                )}
                                {guest.idBackPhoto && (
                                  <MDBox sx={{ flexShrink: 0 }}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      fontWeight="medium"
                                      display="block"
                                      mb={0.5}
                                    >
                                      ID Back
                                    </MDTypography>
                                    <MDBox
                                      component="img"
                                      src={guest.idBackPhoto}
                                      alt={`Guest ${index + 1} ID Back`}
                                      sx={{
                                        width: 140,
                                        height: 95,
                                        objectFit: "cover",
                                        borderRadius: 1.5,
                                        border: "1px solid",
                                        borderColor: "grey.300",
                                        cursor: "pointer",
                                        transition: "opacity 0.2s",
                                        "&:hover": { opacity: 0.85 },
                                      }}
                                      onClick={() => window.open(guest.idBackPhoto, "_blank")}
                                    />
                                  </MDBox>
                                )}
                              </MDBox>
                            )}
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <MDTypography variant="body2" color="text.secondary">
                      No guest details yet. Guest names and photo IDs will appear here after the
                      check-in agent completes check-in with guest names and ID photos.
                    </MDTypography>
                  );
                })()}
              </Card>
            </Grid>

            {/* Criteria Report - Check-in & Check-out confirmation */}
            <Grid item xs={12}>
              <Card sx={{ p: 3, borderRadius: 3, border: "1px solid", borderColor: "grey.200" }}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="info">checklist</Icon> Criteria Report (Check-in & Check-out)
                </MDTypography>
                <MDTypography variant="caption" color="text.secondary" display="block" mb={2}>
                  Items confirmed at check-in (before client) and re-confirmed at check-out
                </MDTypography>

                {/* Admin alert when missing or damaged items exist */}
                {(() => {
                  const hasMissing =
                    reservation.checkOut?.missingItems?.length > 0 ||
                    reservation.checkOut?.criteriaConfirmed?.some(
                      (c) => c.present === false || (c.quantifiable && (c.quantity ?? 0) === 0)
                    );
                  const hasDamaged = reservation.checkOut?.damagedItems?.length > 0;
                  if (hasMissing || hasDamaged) {
                    return (
                      <MDBox mb={2}>
                        {hasMissing && (
                          <Alert
                            severity="error"
                            icon={<Icon>warning</Icon>}
                            sx={{ mb: hasDamaged ? 1 : 0 }}
                          >
                            <MDTypography variant="subtitle2" fontWeight="bold" color="error.dark">
                              Missing items detected – insurance deduction applied. Review details
                              below.
                            </MDTypography>
                          </Alert>
                        )}
                        {hasDamaged && (
                          <Alert severity="warning" icon={<Icon>build</Icon>} sx={{ mb: 0 }}>
                            <MDTypography
                              variant="subtitle2"
                              fontWeight="bold"
                              color="warning.dark"
                            >
                              Damaged items reported .
                            </MDTypography>
                          </Alert>
                        )}
                      </MDBox>
                    );
                  }
                  return null;
                })()}

                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <MDBox>
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={1}
                        color="success.main"
                      >
                        Confirmed at Check-In (before client)
                      </MDTypography>
                      {reservation.checkIn?.criteriaConfirmed?.length > 0 ? (
                        <MDBox component="ul" sx={{ m: 0, pl: 2 }}>
                          {reservation.checkIn.criteriaConfirmed
                            .filter(
                              (c) =>
                                c.present !== false &&
                                (c.quantifiable ? (c.quantity ?? 0) > 0 : true)
                            )
                            .map((c, i) => (
                              <li key={i}>
                                <MDTypography variant="body2">
                                  {c.criterionName || c.criterionNameAr || "Item"}
                                  {c.quantifiable && c.quantity != null && ` (${c.quantity} pcs)`}
                                </MDTypography>
                              </li>
                            ))}
                        </MDBox>
                      ) : (
                        <MDTypography variant="body2" color="text.secondary">
                          No criteria recorded at check-in.
                        </MDTypography>
                      )}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDBox>
                      <MDTypography variant="subtitle2" fontWeight="bold" mb={1} color="info.main">
                        Confirmed at Check-Out
                      </MDTypography>
                      {reservation.checkOut?.criteriaConfirmed?.length > 0 ? (
                        <MDBox component="ul" sx={{ m: 0, pl: 2 }}>
                          {reservation.checkOut.criteriaConfirmed.map((c, i) => {
                            const isAbsent =
                              c.present === false || (c.quantifiable && (c.quantity ?? 0) === 0);
                            const isDamaged = c.absentType === "damaged";
                            return (
                              <li key={i} style={{ marginBottom: 8 }}>
                                <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                  <MDTypography
                                    variant="body2"
                                    color={isAbsent ? "error.main" : "text.primary"}
                                    fontWeight={isAbsent ? 600 : 400}
                                  >
                                    {c.criterionName || c.criterionNameAr || "Item"}
                                    {c.quantifiable && c.quantity != null && ` (${c.quantity} pcs)`}
                                  </MDTypography>
                                  {isAbsent && (
                                    <Chip
                                      label={isDamaged ? "Damaged" : "Missing"}
                                      size="small"
                                      color={isDamaged ? "warning" : "error"}
                                      sx={{ fontWeight: 700 }}
                                    />
                                  )}
                                </MDBox>
                              </li>
                            );
                          })}
                        </MDBox>
                      ) : (
                        <MDTypography variant="body2" color="text.secondary">
                          {reservation.status === "CheckedOut"
                            ? "No criteria recorded at check-out."
                            : "Not yet checked out."}
                        </MDTypography>
                      )}
                    </MDBox>
                  </Grid>
                  {reservation.checkOut?.damagedItems?.length > 0 && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <MDBox
                        sx={{
                          bgcolor: "rgba(237, 108, 2, 0.12)",
                          p: 2.5,
                          borderRadius: 2,
                          border: "2px solid",
                          borderColor: "warning.main",
                          boxShadow: "0 2px 8px rgba(237, 108, 2, 0.15)",
                        }}
                      >
                        <MDTypography
                          variant="subtitle1"
                          fontWeight="bold"
                          mb={1}
                          color="warning.dark"
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Icon sx={{ fontSize: 20 }}>build</Icon>
                          Damaged Items
                        </MDTypography>
                        <MDBox component="ul" sx={{ m: 0, pl: 2.5 }}>
                          {(reservation.checkOut?.damagedItems || []).map((d, i) => (
                            <li key={i} style={{ marginBottom: 4 }}>
                              <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                <MDTypography variant="body2" fontWeight="medium">
                                  <strong>
                                    {(typeof d.name === "string" ? d.name : d.name?.en) || "Item"}
                                  </strong>
                                  {d.description && ` – ${d.description}`}
                                  {d.costSentAt && d.cost != null && (
                                    <>
                                      {" "}
                                      –{" "}
                                      {new Intl.NumberFormat("en-US", {
                                        style: "currency",
                                        currency: reservation.pricing?.currency || "EGP",
                                      }).format(d.cost)}{" "}
                                      (sent)
                                    </>
                                  )}
                                </MDTypography>
                                {d.resolved ? (
                                  <Chip
                                    label="Maintained"
                                    size="small"
                                    color="success"
                                    sx={{ fontWeight: 600 }}
                                  />
                                ) : d.costSentAt ? (
                                  <Chip
                                    label="Cost sent"
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    sx={{ fontWeight: 600 }}
                                  />
                                ) : null}
                              </MDBox>
                            </li>
                          ))}
                        </MDBox>
                      </MDBox>
                    </Grid>
                  )}
                  {(reservation.checkOut?.missingItems?.length > 0 ||
                    (reservation.checkOut?.insuranceDeduction || 0) > 0) && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <MDBox
                        sx={{
                          bgcolor: "rgba(211, 47, 47, 0.12)",
                          p: 2.5,
                          borderRadius: 2,
                          border: "2px solid",
                          borderColor: "error.main",
                          boxShadow: "0 2px 8px rgba(211, 47, 47, 0.15)",
                        }}
                      >
                        <MDTypography
                          variant="subtitle1"
                          fontWeight="bold"
                          mb={1}
                          color="error.dark"
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Icon sx={{ fontSize: 20 }}>warning</Icon>
                          Insurance Deduction (Missing Items) – Admin Attention Required
                        </MDTypography>
                        {(reservation.checkOut?.damagedItemsDeduction || 0) > 0 && (
                          <MDTypography variant="body2" color="warning.main" mb={1}>
                            Additional deduction for damaged items:{" "}
                            {new Intl.NumberFormat("en-US", {
                              style: "currency",
                              currency: reservation.pricing?.currency || "EGP",
                            }).format(reservation.checkOut.damagedItemsDeduction)}
                          </MDTypography>
                        )}
                        <MDTypography variant="body2" mb={1.5} fontWeight="medium">
                          Total deducted:{" "}
                          {new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: reservation.pricing?.currency || "EGP",
                          }).format(reservation.checkOut?.insuranceDeduction || 0)}
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          fontWeight="bold"
                          display="block"
                          mb={0.5}
                          color="error.dark"
                        >
                          Missing items (deducted from client insurance):
                        </MDTypography>
                        <MDBox component="ul" sx={{ m: 0, pl: 2.5 }}>
                          {(reservation.checkOut?.missingItems || []).map((m, i) => (
                            <li key={i} style={{ marginBottom: 4 }}>
                              <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                <MDTypography variant="body2" fontWeight="medium">
                                  {m.name}
                                  {m.quantity > 1 && ` (${m.quantity} pcs)`}
                                  {" – "}
                                  {new Intl.NumberFormat("en-US", {
                                    style: "currency",
                                    currency: reservation.pricing?.currency || "EGP",
                                  }).format(m.clientPrice || 0)}
                                </MDTypography>
                                {m.replaced && (
                                  <Chip
                                    label="Replaced"
                                    size="small"
                                    color="success"
                                    sx={{ fontWeight: 600 }}
                                  />
                                )}
                              </MDBox>
                            </li>
                          ))}
                        </MDBox>
                      </MDBox>
                    </Grid>
                  )}
                </Grid>
              </Card>
            </Grid>

            {/* Housekeeping Report – before/after photos, duration, staff (Admin/Super Admin) */}
            <Grid item xs={12}>
              <Card
                sx={{ p: 3, borderRadius: 3, border: "1px solid", borderColor: "success.light" }}
              >
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="success">cleaning_services</Icon> Housekeeping Report
                </MDTypography>
                <Divider sx={{ mb: 2 }} />
                {!reservation.housekeeping || reservation.housekeeping.status === "Pending" ? (
                  <MDTypography variant="body2" color="text.secondary">
                    {reservation.status === "CheckedOut"
                      ? "Cleaning not started yet. Housekeeping will upload before/after photos and record time when they start and complete."
                      : "Housekeeping report will appear after guest checks out and housekeeping completes cleaning."}
                  </MDTypography>
                ) : (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                      >
                        <MDTypography variant="body2" color="text.secondary">
                          Status
                        </MDTypography>
                        <Chip
                          label={reservation.housekeeping.status}
                          size="small"
                          color={
                            reservation.housekeeping.status === "Completed" ? "success" : "info"
                          }
                          sx={{ fontWeight: 600 }}
                        />
                      </MDBox>
                      {reservation.housekeeping.startedAt && (
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                        >
                          <MDTypography variant="body2" color="text.secondary">
                            Started
                          </MDTypography>
                          <MDTypography variant="body2" fontWeight="medium">
                            {formatDateTime(reservation.housekeeping.startedAt)}
                          </MDTypography>
                        </MDBox>
                      )}
                      {reservation.housekeeping.completedAt && (
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                        >
                          <MDTypography variant="body2" color="text.secondary">
                            Completed
                          </MDTypography>
                          <MDTypography variant="body2" fontWeight="medium">
                            {formatDateTime(reservation.housekeeping.completedAt)}
                          </MDTypography>
                        </MDBox>
                      )}
                      {reservation.housekeeping.cleaningDurationMinutes != null &&
                        reservation.housekeeping.cleaningDurationMinutes >= 0 && (
                          <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={1}
                          >
                            <MDTypography variant="body2" color="text.secondary">
                              Cleaning duration
                            </MDTypography>
                            <MDTypography variant="body2" fontWeight="bold" color="success.main">
                              {reservation.housekeeping.cleaningDurationMinutes < 60
                                ? `${reservation.housekeeping.cleaningDurationMinutes} min`
                                : `${Math.floor(
                                    reservation.housekeeping.cleaningDurationMinutes / 60
                                  )}h ${reservation.housekeeping.cleaningDurationMinutes % 60} min`}
                            </MDTypography>
                          </MDBox>
                        )}
                      {reservation.housekeeping.startedBy?.fullName && (
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                        >
                          <MDTypography variant="body2" color="text.secondary">
                            Started by
                          </MDTypography>
                          <MDTypography variant="body2" fontWeight="medium">
                            {reservation.housekeeping.startedBy.fullName}
                          </MDTypography>
                        </MDBox>
                      )}
                      {reservation.housekeeping.completedBy?.fullName && (
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                        >
                          <MDTypography variant="body2" color="text.secondary">
                            Completed by
                          </MDTypography>
                          <MDTypography variant="body2" fontWeight="medium">
                            {reservation.housekeeping.completedBy.fullName}
                          </MDTypography>
                        </MDBox>
                      )}
                      {reservation.housekeeping.notes && (
                        <MDBox mt={1.5}>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={0.5}
                          >
                            Notes
                          </MDTypography>
                          <MDTypography variant="body2">
                            {reservation.housekeeping.notes}
                          </MDTypography>
                        </MDBox>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {reservation.housekeeping.beforePhotos?.length > 0 && (
                        <MDBox mb={2}>
                          <MDTypography
                            variant="subtitle2"
                            fontWeight="bold"
                            mb={1}
                            color="text.secondary"
                          >
                            Before cleaning ({reservation.housekeeping.beforePhotos.length})
                          </MDTypography>
                          <Grid container spacing={1}>
                            {reservation.housekeeping.beforePhotos.slice(0, 6).map((url, i) => (
                              <Grid item xs={6} sm={4} key={i}>
                                <MDBox
                                  component="img"
                                  src={url}
                                  alt={`Before ${i + 1}`}
                                  sx={{
                                    width: "100%",
                                    height: 80,
                                    objectFit: "cover",
                                    borderRadius: 1,
                                    cursor: "pointer",
                                    border: "1px solid",
                                    borderColor: "divider",
                                  }}
                                  onClick={() => window.open(url, "_blank")}
                                />
                              </Grid>
                            ))}
                          </Grid>
                          {reservation.housekeeping.beforePhotos.length > 6 && (
                            <MDTypography variant="caption" color="text.secondary">
                              +{reservation.housekeeping.beforePhotos.length - 6} more
                            </MDTypography>
                          )}
                        </MDBox>
                      )}
                      {reservation.housekeeping.afterPhotos?.length > 0 && (
                        <MDBox>
                          <MDTypography
                            variant="subtitle2"
                            fontWeight="bold"
                            mb={1}
                            color="text.secondary"
                          >
                            After cleaning ({reservation.housekeeping.afterPhotos.length})
                          </MDTypography>
                          <Grid container spacing={1}>
                            {reservation.housekeeping.afterPhotos.slice(0, 6).map((url, i) => (
                              <Grid item xs={6} sm={4} key={i}>
                                <MDBox
                                  component="img"
                                  src={url}
                                  alt={`After ${i + 1}`}
                                  sx={{
                                    width: "100%",
                                    height: 80,
                                    objectFit: "cover",
                                    borderRadius: 1,
                                    cursor: "pointer",
                                    border: "1px solid",
                                    borderColor: "divider",
                                  }}
                                  onClick={() => window.open(url, "_blank")}
                                />
                              </Grid>
                            ))}
                          </Grid>
                          {reservation.housekeeping.afterPhotos.length > 6 && (
                            <MDTypography variant="caption" color="text.secondary">
                              +{reservation.housekeeping.afterPhotos.length - 6} more
                            </MDTypography>
                          )}
                        </MDBox>
                      )}
                    </Grid>
                  </Grid>
                )}
              </Card>
            </Grid>

            {/* Booking Contact - Client email/phone (secondary) */}
            <Grid item xs={12}>
              <Card sx={{ p: 3, borderRadius: 3 }}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="secondary">contact_mail</Icon> Booking Contact
                </MDTypography>
                <Divider sx={{ mb: 2 }} />
                {reservation.client?.fullName && (
                  <MDBox mb={1}>
                    <MDTypography variant="caption" color="text.secondary" display="block">
                      Name
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="medium">
                      {reservation.client.fullName}
                    </MDTypography>
                  </MDBox>
                )}
                <MDBox mb={reservation.client?.phone ? 1 : 0}>
                  <MDTypography variant="caption" color="text.secondary" display="block">
                    Email
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {reservation.client?.email || "N/A"}
                  </MDTypography>
                </MDBox>
                {reservation.client?.phone && (
                  <MDBox>
                    <MDTypography variant="caption" color="text.secondary" display="block">
                      Phone
                    </MDTypography>
                    <MDTypography variant="body2">{reservation.client.phone}</MDTypography>
                  </MDBox>
                )}
              </Card>
            </Grid>
          </Grid>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ReservationDetails;
